export interface Health {
  status: 'ok'
  version: string
  environment: string
}

export interface Readiness {
  status: 'ok' | 'degraded'
  database: {
    connected: boolean
    dialect: string
    error: string | null
  }
}

export type UserRole = 'admin' | 'observer' | 'employee'

export interface UserProfile {
  id: number
  email: string
  role: UserRole
  is_active: boolean
  last_login_at: string | null
}

export interface UserAccount extends UserProfile {
  employee_id: number | null
  created_at: string
}

/** Ошибка от API с разобранным сообщением. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function extractDetail(body: unknown, fallback: string): string {
  if (typeof body !== 'object' || body === null || !('detail' in body)) {
    return fallback
  }
  const detail = (body as { detail: unknown }).detail
  if (typeof detail === 'string') {
    return detail
  }
  // Ошибки валидации FastAPI приходят списком объектов.
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (typeof item === 'object' && item && 'msg' in item ? String(item.msg) : null))
      .filter((msg): msg is string => Boolean(msg))
    if (messages.length > 0) {
      return messages.join('; ')
    }
  }
  return fallback
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  })

  if (!response.ok) {
    let body: unknown = null
    try {
      body = await response.json()
    } catch {
      // Тело может быть пустым — оставляем сообщение по умолчанию.
    }
    throw new ApiError(extractDetail(body, `Ошибка ${response.status}`), response.status)
  }

  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

export const fetchHealth = () => request<Health>('/api/health')
export const fetchReadiness = () => request<Readiness>('/api/ready')

export const fetchMe = () => request<UserProfile>('/api/auth/me')

export const login = (email: string, password: string) =>
  request<UserProfile>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })

export const logout = () => request<void>('/api/auth/logout', { method: 'POST' })

export const changeOwnPassword = (currentPassword: string, newPassword: string) =>
  request<void>('/api/auth/password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  })

export const fetchUsers = () => request<UserAccount[]>('/api/users')

export const createUser = (
  email: string,
  password: string,
  role: UserRole,
  employeeId?: number | null,
) =>
  request<UserAccount>('/api/users', {
    method: 'POST',
    body: JSON.stringify({ email, password, role, employee_id: employeeId ?? null }),
  })

export const setUserActive = (id: number, isActive: boolean) =>
  request<UserAccount>(`/api/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: isActive }),
  })

export const resetUserPassword = (id: number, newPassword: string) =>
  request<void>(`/api/users/${id}/password`, {
    method: 'POST',
    body: JSON.stringify({ new_password: newPassword }),
  })

export type AuditActionKind = 'create' | 'update' | 'delete'

export interface FieldChange {
  field: string
  before: unknown
  after: unknown
}

export interface AuditEntry {
  id: number
  request_id: string
  actor_email: string | null
  entity_type: string
  entity_id: number | null
  entity_label: string | null
  action: AuditActionKind
  changes: FieldChange[]
  ip: string | null
  created_at: string
}

export interface AuditPage {
  total: number
  entries: AuditEntry[]
  entity_types: string[]
}

export interface AuditFilters {
  entityType?: string
  actorUserId?: number
  requestId?: string
  limit?: number
  offset?: number
}

export interface ChartNode {
  id: string
  kind: 'employee' | 'department'
  employee_id: number | null
  department_id: number | null
  title: string
  subtitle: string | null
  department: string | null
  /** Код направления: по нему группируются области подчинения на схеме. */
  business_line: string | null
  /** Ключ палитры, а не цвет — светлую и тёмную тему разводит CSS. */
  color: string
  headcount: number | null
  in_management_structure: boolean
  reports_count: number
}

export interface ChartEdge {
  id: string
  source: string
  target: string
  kind: 'management' | 'cross'
  label: string | null
  link_type: string | null
}

export interface Chart {
  nodes: ChartNode[]
  edges: ChartEdge[]
  off_chart_employees: number
}

/** Ключи палитры направлений. Значения должны совпадать с `LineColor` на бэкенде. */
export const LINE_COLORS = ['indigo', 'teal', 'amber', 'rose', 'violet', 'slate'] as const

export type LineColor = (typeof LINE_COLORS)[number]

/** Подписи цветов для формы. Сам цвет виден рядом образцом, поэтому названия нейтральные. */
export const LINE_COLOR_LABELS: Record<LineColor, string> = {
  indigo: 'Синий',
  teal: 'Бирюзовый',
  amber: 'Янтарный',
  rose: 'Розовый',
  violet: 'Фиолетовый',
  slate: 'Серый',
}

export interface BusinessLine {
  id: number
  code: string
  name: string
  color: LineColor
  sort_order: number
  is_active: boolean
}

export interface EmployeeBrief {
  id: number
  display_name: string
  department: string | null
  role: string | null
  is_active: boolean
  on_chart: boolean
}

export interface EmploymentPeriod {
  id: number
  hired_at: string | null
  /** null — период открыт, человек работает. */
  left_at: string | null
  reason: string | null
}

export interface EmployeeDetail extends EmployeeBrief {
  department_id: number | null
  position: string | null
  position_id: number | null
  business_line_id: number | null
  /** Название направления; идентификатор — в `business_line_id`. */
  business_line: string | null
  in_management_structure: boolean
  manager: EmployeeBrief | null
  reports: EmployeeBrief[]
  /** История найма и увольнений, от свежего к старому. */
  employment: EmploymentPeriod[]
}

export interface EmploymentChanged {
  employee: EmployeeDetail
  /** Сколько активных подчинённых осталось без руководителя после увольнения. */
  reports_left_without_manager: number
}

export interface DepartmentOut {
  id: number
  code: string
  name: string
  parent_id: number | null
  business_line_id: number | null
  business_line: string | null
  /** Показывать подразделение одним узлом с численностью вместо всех его людей. */
  is_collapsed: boolean
  head_employee_id: number | null
  employee_count: number
}

export interface OrgLinkOut {
  id: number
  from_employee_id: number
  from_name: string
  to_employee_id: number | null
  to_department_id: number | null
  to_name: string
  type: string
  label: string | null
}

export const fetchChart = (includeDismissed = false) =>
  request<Chart>(`/api/org/chart?include_dismissed=${includeDismissed}`)

/** Как шаг нашёл свой узел на схеме. `department_head` — вывод, а не факт. */
export type ThreadResolution = 'employee' | 'collapsed_department' | 'department_head'

export interface ThreadStep {
  key: string
  title: string
  type: ProcessNodeType
  /** Номер шага в обходе процесса, с единицы. */
  order: number
  /** Узел схемы, на который лёг шаг. `null` — шаг на схему не лёг. */
  chart_node_id: string | null
  resolved_via: ThreadResolution | null
  unplaced_reason: string | null
}

export interface ThreadHop {
  id: string
  /** Концы — шаги, а не карточки: нить проходит через кружки этапов. */
  from_step: string
  to_step: string
  /** Карточки, в которых стоят кружки концов — чтобы знать, не скрыты ли они. */
  source: string
  target: string
  label: string | null
}

export interface ChartThread {
  process_id: number
  process_name: string
  steps: ThreadStep[]
  hops: ThreadHop[]
}

export const fetchChartThread = (processId: number, includeDismissed = false) =>
  request<ChartThread>(
    `/api/org/chart/thread?process_id=${processId}&include_dismissed=${includeDismissed}`,
  )

export const searchEmployees = (query: string, includeDismissed = false) =>
  request<EmployeeBrief[]>(
    `/api/org/employees?q=${encodeURIComponent(query)}&include_dismissed=${includeDismissed}`,
  )

export const fetchEmployee = (id: number) => request<EmployeeDetail>(`/api/org/employees/${id}`)

export const updateEmployee = (id: number, patch: Record<string, unknown>) =>
  request<EmployeeDetail>(`/api/org/employees/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })

export const createEmployee = (payload: Record<string, unknown>) =>
  request<EmployeeDetail>('/api/org/employees', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const dismissEmployee = (id: number, leftAt: string | null, reason: string | null) =>
  request<EmploymentChanged>(`/api/org/employees/${id}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ left_at: leftAt || null, reason: reason || null }),
  })

export const rehireEmployee = (id: number, hiredAt: string | null) =>
  request<EmploymentChanged>(`/api/org/employees/${id}/rehire`, {
    method: 'POST',
    body: JSON.stringify({ hired_at: hiredAt || null }),
  })

export const fetchBusinessLines = () => request<BusinessLine[]>('/api/org/business-lines')

export const createBusinessLine = (payload: Record<string, unknown>) =>
  request<BusinessLine>('/api/org/business-lines', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const updateBusinessLine = (id: number, patch: Record<string, unknown>) =>
  request<BusinessLine>(`/api/org/business-lines/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })

export const fetchDepartments = () => request<DepartmentOut[]>('/api/org/departments')

export const updateDepartment = (id: number, patch: Record<string, unknown>) =>
  request<DepartmentOut>(`/api/org/departments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })

export const createDepartment = (payload: Record<string, unknown>) =>
  request<DepartmentOut>('/api/org/departments', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const fetchOrgLinks = () => request<OrgLinkOut[]>('/api/org/links')

export const createOrgLink = (payload: Record<string, unknown>) =>
  request<OrgLinkOut>('/api/org/links', { method: 'POST', body: JSON.stringify(payload) })

export const deleteOrgLink = (id: number) =>
  request<void>(`/api/org/links/${id}`, { method: 'DELETE' })

// ---------------------------------------------------------------------------
// Бизнес-процессы
// ---------------------------------------------------------------------------

/** Значения должны совпадать с `ProcessSchedule` на бэкенде. */
export type ProcessSchedule =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'annually'
  | 'on_demand'

export const SCHEDULE_LABELS: Record<ProcessSchedule, string> = {
  daily: 'Ежедневно',
  weekly: 'Еженедельно',
  monthly: 'Ежемесячно',
  quarterly: 'Ежеквартально',
  annually: 'Ежегодно',
  on_demand: 'По необходимости',
}

export type IntensityPeriod = 'day' | 'week' | 'month' | 'quarter' | 'year'

/** Родительный падеж: подставляется в «5 раз в месяц». */
export const PERIOD_LABELS: Record<IntensityPeriod, string> = {
  day: 'день',
  week: 'неделю',
  month: 'месяц',
  quarter: 'квартал',
  year: 'год',
}

export type ProcessStatus = 'draft' | 'active' | 'archived'

export const STATUS_LABELS: Record<ProcessStatus, string> = {
  draft: 'Черновик',
  active: 'Действует',
  archived: 'В архиве',
}

export type ProcessNodeType = 'start' | 'task' | 'decision' | 'end'

export const NODE_TYPE_LABELS: Record<ProcessNodeType, string> = {
  start: 'Начало',
  task: 'Этап',
  decision: 'Развилка',
  end: 'Конец',
}

export interface ProcessSummary {
  id: number
  name: string
  description: string | null
  schedule: ProcessSchedule
  expected_count: number | null
  expected_period: IntensityPeriod | null
  actual_count: number | null
  actual_period: IntensityPeriod | null
  status: ProcessStatus
  owner_employee_id: number | null
  owner_name: string | null
  node_count: number
  created_at: string
  updated_at: string
}

export interface ProcessParticipant {
  id: number
  employee_id: number | null
  department_id: number | null
  /** Имя человека или название подразделения — смотря чем участник является. */
  name: string
  note: string | null
}

export interface ProcessStage {
  id: number
  key: string
  type: ProcessNodeType
  title: string
  description: string | null
  order_index: number
  actor_employee_id: number | null
  actor_name: string | null
  /** Подразделение, назначенное этапу явно. */
  department_id: number | null
  /** Вычисленный лейн нити: явное подразделение → подразделение должности → своё. */
  lane_department_id: number | null
  lane_department: string | null
}

export interface ProcessTransition {
  id: number
  from_node_key: string
  to_node_key: string
  /** Условие перехода — оно же подпись стрелки. */
  label: string | null
}

export interface ProcessDetail extends ProcessSummary {
  participants: ProcessParticipant[]
  nodes: ProcessStage[]
  edges: ProcessTransition[]
  /** Что мешает считать процесс описанным до конца. Не запрещает сохранять. */
  problems: string[]
}

export interface ProcessFilters {
  status?: ProcessStatus
  schedule?: ProcessSchedule
  q?: string
}

export const fetchProcesses = (filters: ProcessFilters = {}) => {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.schedule) params.set('schedule', filters.schedule)
  if (filters.q?.trim()) params.set('q', filters.q.trim())
  const query = params.toString()
  return request<ProcessSummary[]>(`/api/processes${query ? `?${query}` : ''}`)
}

export const fetchProcess = (id: number) => request<ProcessDetail>(`/api/processes/${id}`)

export const createProcess = (payload: Record<string, unknown>) =>
  request<ProcessDetail>('/api/processes', { method: 'POST', body: JSON.stringify(payload) })

export const updateProcess = (id: number, patch: Record<string, unknown>) =>
  request<ProcessDetail>(`/api/processes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })

export const addParticipant = (processId: number, payload: Record<string, unknown>) =>
  request<ProcessDetail>(`/api/processes/${processId}/participants`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const removeParticipant = (processId: number, participantId: number) =>
  request<ProcessDetail>(`/api/processes/${processId}/participants/${participantId}`, {
    method: 'DELETE',
  })

export const addStage = (processId: number, payload: Record<string, unknown>) =>
  request<ProcessDetail>(`/api/processes/${processId}/nodes`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const updateStage = (processId: number, stageId: number, patch: Record<string, unknown>) =>
  request<ProcessDetail>(`/api/processes/${processId}/nodes/${stageId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })

export const deleteStage = (processId: number, stageId: number) =>
  request<ProcessDetail>(`/api/processes/${processId}/nodes/${stageId}`, { method: 'DELETE' })

/**
 * Задаёт порядок этапов целиком.
 *
 * Список, а не «переместить вверх»: перестановка задевает соседей, и сервер обязан
 * получить тот порядок, который видел пользователь, а не досчитывать его сам. Должен
 * перечислять все этапы ровно по одному разу.
 */
export const reorderStages = (processId: number, stageIds: number[]) =>
  request<ProcessDetail>(`/api/processes/${processId}/nodes/order`, {
    method: 'PUT',
    body: JSON.stringify({ node_ids: stageIds }),
  })

export const addTransition = (processId: number, payload: Record<string, unknown>) =>
  request<ProcessDetail>(`/api/processes/${processId}/edges`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const updateTransition = (processId: number, edgeId: number, label: string | null) =>
  request<ProcessDetail>(`/api/processes/${processId}/edges/${edgeId}`, {
    method: 'PATCH',
    body: JSON.stringify({ label }),
  })

export const deleteTransition = (processId: number, edgeId: number) =>
  request<ProcessDetail>(`/api/processes/${processId}/edges/${edgeId}`, { method: 'DELETE' })

export const fetchAudit = (filters: AuditFilters = {}) => {
  const params = new URLSearchParams()
  if (filters.entityType) params.set('entity_type', filters.entityType)
  if (filters.actorUserId !== undefined) params.set('actor_user_id', String(filters.actorUserId))
  if (filters.requestId) params.set('request_id', filters.requestId)
  params.set('limit', String(filters.limit ?? 50))
  params.set('offset', String(filters.offset ?? 0))
  return request<AuditPage>(`/api/audit?${params.toString()}`)
}
